<template>
  <v-card>
    <v-toolbar flat dark color="primary">
      <v-btn icon dark @click="$emit('close')">
        <v-icon>close</v-icon>
      </v-btn>
      <v-toolbar-title>Inspection Records</v-toolbar-title>
    </v-toolbar>

    <v-card-text class="pt-4">
      <div class="mb-4">
        <div class="caption grey--text">TRUCK</div>
        <div class="title">{{ truck ? truckName : "No truck" }}</div>
        <div class="caption grey--text mt-1">
          Last 14 days ({{ rangeLabel }})
        </div>
      </div>

      <v-alert v-if="error" type="error" text dense>{{ error }}</v-alert>

      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <template v-else>
        <div v-for="day in days" :key="day.date" class="mb-2">
          <!-- No inspection on this date -->
          <v-sheet
            v-if="!day.inspections.length"
            outlined
            rounded
            class="pa-3 d-flex align-center grey--text"
          >
            <div class="flex-grow-1">
              <div class="body-2">{{ day.displayDate }}</div>
              <div class="caption">Not in operation</div>
            </div>
          </v-sheet>

          <!-- One or more inspections on this date -->
          <v-expansion-panels v-else accordion flat>
            <v-expansion-panel
              v-for="insp in day.inspections"
              :key="insp.id"
              class="mb-1 record-panel"
            >
              <v-expansion-panel-header>
                <div>
                  <div class="body-2 font-weight-medium">
                    {{ day.displayDate }}
                  </div>
                  <div class="caption grey--text">
                    {{ insp.driver.firstName }} {{ insp.driver.lastName }}
                  </div>
                </div>
                <template v-slot:actions>
                  <v-chip
                    x-small
                    :color="insp.hasDefects ? 'warning' : 'success'"
                    text-color="white"
                  >
                    {{
                      insp.hasDefects
                        ? insp.defects.length +
                          (insp.defects.length === 1 ? " defect" : " defects")
                        : "No defects"
                    }}
                  </v-chip>
                </template>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <div v-if="insp.hasDefects" class="mb-3">
                  <div class="caption grey--text">DEFECTS</div>
                  <div class="red--text font-weight-medium">
                    {{ insp.defects.map((d) => d.label).join(", ") }}
                  </div>
                </div>

                <div class="mb-3">
                  <div class="caption grey--text">REMARKS</div>
                  <div class="body-2">{{ insp.remarks || "None" }}</div>
                </div>

                <div class="mb-3">
                  <div class="caption grey--text">TRAILER</div>
                  <div class="body-2">
                    {{ insp.noTrailer ? "No trailer" : insp.trailerNumber || "Yes" }}
                  </div>
                </div>

                <div class="mb-3">
                  <div class="caption grey--text mb-1">ITEMS</div>
                  <v-row dense>
                    <v-col
                      cols="6"
                      v-for="item in insp.items"
                      :key="item.key"
                      class="py-0"
                    >
                      <span
                        class="caption"
                        :class="
                          item.status === 'defect'
                            ? 'red--text font-weight-bold'
                            : 'grey--text text--darken-1'
                        "
                      >
                        {{ item.status === "defect" ? "✕" : "✓" }}
                        {{ item.label }}
                      </span>
                    </v-col>
                  </v-row>
                </div>

                <div v-if="insp.signature && insp.signature.signatureData">
                  <div class="caption grey--text">DRIVER SIGNATURE</div>
                  <img
                    :src="insp.signature.signatureData"
                    style="max-width: 220px; border: 1px solid #ccc;"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import fb from "@/main";
import utils from "@/helpers/utils";
import moment from "moment";

const DAYS = 14;

export default {
  props: {
    truck: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      inspections: [],
      loading: true,
      error: "",
    };
  },
  computed: {
    truckName() {
      return utils.truckName(this.truck);
    },
    rangeLabel() {
      const start = moment().subtract(DAYS - 1, "days");
      return `${start.format("MMM D")} to ${moment().format("MMM D")}`;
    },
    // BIC requires the reports to be available for the last 14 days. Days the
    // truck was not used have no report, which BIC allows, so those are shown
    // as not in operation rather than left blank.
    days() {
      const byDate = {};
      this.inspections.forEach((i) => {
        if (!byDate[i.date]) byDate[i.date] = [];
        byDate[i.date].push(i);
      });
      const out = [];
      for (let d = 0; d < DAYS; d++) {
        const day = moment().subtract(d, "days");
        const date = day.format("YYYY-MM-DD");
        out.push({
          date,
          displayDate: day.format("ddd, MMM D"),
          inspections: byDate[date] || [],
        });
      }
      return out;
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    async load() {
      if (!this.truck) {
        this.loading = false;
        return;
      }
      const cutoff = moment()
        .subtract(DAYS - 1, "days")
        .format("YYYY-MM-DD");
      try {
        // Scoped to the truck, not the driver. The record has to travel with
        // the vehicle, so another driver's inspection on this truck counts.
        const res = await fb.db
          .collection("preTripInspections")
          .where("truck.id", "==", this.truck.id)
          .where("date", ">=", cutoff)
          .orderBy("date", "desc")
          .get();
        const inspections = [];
        res.forEach((doc) => {
          inspections.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        this.inspections = inspections;
      } catch (error) {
        console.log("Error loading inspection records:", error);
        this.error = "Could not load inspection records. Please try again.";
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.record-panel {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
